<template>
  <div class="flex items-center gap-1">
    <USelect
      v-model="prefix"
      :items="prefixOptions"
      value-key="value"
      label-key="label"
      :disabled="disabled"
      color="neutral"
      highlight
      :ui="{
        base: 'h-7 text-sm cursor-pointer',
        content: 'min-w-[7.5rem]',
        item: 'cursor-pointer items-center',
        itemTrailingIcon: 'hidden'
      }"
      class="w-26 shrink-0"
    >
      <template #leading>
        <span class="phone-flag-wrapper">
          <UIcon
            :name="selectedCountry.flag"
            mode="svg"
            class="phone-flag"
          />
        </span>
      </template>
      <template #item-leading="{ item }">
        <span class="phone-flag-wrapper">
          <UIcon
            :name="item.flag"
            mode="svg"
            class="phone-flag"
          />
        </span>
      </template>
    </USelect>

    <div class="relative">
      <UInput
        v-model="digits"
        v-maska="selectedCountry.mask"
        type="tel"
        inputmode="numeric"
        autocomplete="tel-national"
        :disabled="disabled"
        color="neutral"
        highlight
        placeholder=""
        class="h-7 text-sm"
        :class="inputClass"
        :ui="{ base: 'peer' }"
      >
        <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
          <span class="inline-flex bg-default px-1 rounded">{{ label }}</span>
        </label>
      </UInput>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { vMaska } from 'maska/vue'
import { composePhoneNumber, defaultPhoneCountry, digitsOnly, formatPhoneDigits, getPhoneCountry, parsePhoneNumber, selectablePhoneCountries} from '@/utils/phone'

const props = withDefaults(defineProps<{
  label?: string
  defaultPrefix?: string
  disabled?: boolean
  inputClass?: string
}>(), {
  label: '',
  defaultPrefix: defaultPhoneCountry.prefix,
  disabled: false,
  inputClass: 'w-30'
})

const model = defineModel<string>({ default: '' })

const initial = parsePhoneNumber(model.value, props.defaultPrefix)
const prefix = ref(initial.prefix)
const digits = ref(initial.digits)

const selectedCountry = computed(() => getPhoneCountry(prefix.value))

const prefixOptions = computed(() => {
  const countries = selectablePhoneCountries.some(country => country.prefix === prefix.value) ? selectablePhoneCountries : [...selectablePhoneCountries, selectedCountry.value]
  
  return countries.map(country => ({
    value: country.prefix,
    label: country.prefix,
    flag: country.flag
  }))
})

watch(prefix, (value) => {
  digits.value = formatPhoneDigits(digitsOnly(digits.value), getPhoneCountry(value))
})

watch([prefix, digits], ([currentPrefix, currentDigits]) => {
  model.value = composePhoneNumber(currentPrefix, currentDigits)
})

watch(model, (value) => {
  if (value === composePhoneNumber(prefix.value, digits.value)) return
  
  const parsed = parsePhoneNumber(value, props.defaultPrefix)
  
  prefix.value = parsed.prefix
  digits.value = parsed.digits
})
</script>

<style scoped>
.phone-flag-wrapper {
  display: inline-flex;
  border-radius: 2px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
  line-height: 0;
}

:root.dark .phone-flag-wrapper {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.phone-flag {
  width: 16px;
  height: 12px;
}
</style>
